/**
 * Проверки перед генерацией и панель готовности (ТЗ раздел 6.8, FR-04, FR-32).
 *
 * Один и тот же расчёт используется:
 *   • клиентом  — для панели готовности и подсветки блоков;
 *   • сервером  — как последний рубеж: битый профиль не должен стать документом.
 */

import type { CaseProfile } from '../types/profile.js';
import { innError } from '../validators/inn.js';
import { snilsError } from '../validators/snils.js';
import { isAdult, isBefore, isPast, isWithinThreeYears } from '../validators/dates.js';
import { computeTotals, creditorsTotalsConsistent } from './totals.js';
import { DEBT_THRESHOLD_KOPECKS } from './constants.js';
import { findDuplicateCreditors } from './creditor-dictionary.js';
import { similarSurname } from './family-names.js';

/** Блоки карточки — совпадают с боковой навигацией (ТЗ FR-02). */
export type BlockId =
  | 'debtor'
  | 'court'
  | 'representative'
  | 'creditors'
  | 'property'
  | 'deals'
  | 'family'
  | 'income'
  | 'legal';

export const BLOCK_TITLES: Record<BlockId, string> = {
  debtor: 'Должник',
  court: 'Суд',
  representative: 'Представитель',
  creditors: 'Кредиторы',
  property: 'Имущество',
  deals: 'Сделки',
  family: 'Семья',
  income: 'Доходы и занятость',
  legal: 'Процессуальное',
};

export type IssueSeverity = 'blocking' | 'warning';

export interface Issue {
  severity: IssueSeverity;
  block: BlockId;
  /** Путь к полю в профиле — по нему интерфейс подсвечивает конкретный ввод */
  path: string;
  message: string;
}

export interface Readiness {
  issues: Issue[];
  blocking: Issue[];
  warnings: Issue[];
  /** Состояние по блокам для панели готовности (FR-04) */
  blocks: Record<BlockId, 'empty' | 'filled' | 'errors' | 'warnings'>;
  canGenerate: boolean;
  totalKopecks: number;
  creditorsCount: number;
}

const req = (value: unknown): boolean =>
  value !== null && value !== undefined && String(value).trim() !== '';

export function computeReadiness(profile: CaseProfile, now = new Date()): Readiness {
  const issues: Issue[] = [];
  const add = (severity: IssueSeverity, b: BlockId, path: string, message: string) =>
    issues.push({ severity, block: b, path, message });

  const d = profile.debtor;

  // ---- Должник -------------------------------------------------------------
  if (!req(d.last)) add('blocking', 'debtor', 'debtor.last', 'Не указана фамилия');
  if (!req(d.first)) add('blocking', 'debtor', 'debtor.first', 'Не указано имя');
  if (!d.gender)
    add('blocking', 'debtor', 'debtor.gender', 'Не указан пол — без него не согласуется род в формулировках');
  if (!req(d.birthDate)) add('blocking', 'debtor', 'debtor.birthDate', 'Не указана дата рождения');
  else if (!isPast(d.birthDate, now))
    add('blocking', 'debtor', 'debtor.birthDate', 'Дата рождения должна быть в прошлом');
  else if (!isAdult(d.birthDate, now))
    add('blocking', 'debtor', 'debtor.birthDate', 'Должник несовершеннолетний');
  if (!req(d.birthPlace)) add('blocking', 'debtor', 'debtor.birthPlace', 'Не указано место рождения');

  const innMsg = innError(d.inn, 'person');
  if (innMsg) add('blocking', 'debtor', 'debtor.inn', innMsg);

  const snilsMsg = snilsError(d.snils);
  if (snilsMsg) add('blocking', 'debtor', 'debtor.snils', snilsMsg);

  if (!req(d.idDocument.series) || !req(d.idDocument.number))
    add('blocking', 'debtor', 'debtor.idDocument', 'Не указаны серия и номер документа, удостоверяющего личность');
  if (!req(d.idDocument.issuedBy))
    add('blocking', 'debtor', 'debtor.idDocument.issuedBy', 'Не указано, кем выдан документ');
  if (!req(d.idDocument.issuedAt))
    add('blocking', 'debtor', 'debtor.idDocument.issuedAt', 'Не указана дата выдачи документа');

  if (d.hasRegistration) {
    if (!req(d.registrationAddress.full))
      add('blocking', 'debtor', 'debtor.registrationAddress', 'Не указан адрес регистрации');
  } else if (!req(d.stayAddress?.full)) {
    add('blocking', 'debtor', 'debtor.stayAddress', 'При отсутствии регистрации указывается адрес места пребывания');
  }

  // Падежные формы — раздел 6.8: «не заданы падежные формы ФИО»
  const casesMissing = (['nominative', 'genitive', 'dative'] as const).filter(
    (c) => !req(profile.declension.fio[c]),
  );
  if (casesMissing.length > 0)
    add('blocking', 'debtor', 'declension.fio', 'Не заданы падежные формы ФИО — проверьте блок склонений');

  // ---- Суд -----------------------------------------------------------------
  if (!req(profile.court.name)) add('blocking', 'court', 'court.name', 'Не выбран арбитражный суд');
  if (!req(profile.court.address)) add('warning', 'court', 'court.address', 'Не заполнен почтовый адрес суда');

  // ---- Представитель -------------------------------------------------------
  if (profile.representative.enabled) {
    if (!req(profile.representative.name))
      add('blocking', 'representative', 'representative.name', 'Не указано наименование представителя');
    if (!req(profile.representative.powerOfAttorney))
      add('warning', 'representative', 'representative.powerOfAttorney', 'Не указаны реквизиты доверенности');
    const repInn = profile.representative.inn ? innError(profile.representative.inn) : null;
    if (repInn) add('blocking', 'representative', 'representative.inn', 'Представитель: ' + repInn);
  }

  // ---- Кредиторы -----------------------------------------------------------
  const totals = computeTotals(profile);
  if (totals.creditorsCount === 0)
    add('blocking', 'creditors', 'creditors', 'Список кредиторов пуст: не добавлено ни одного обязательства');

  profile.creditors.forEach((c, i) => {
    const p = `creditors[${i}]`;
    const row = `Строка ${i + 1}: `;
    if (!req(c.name)) add('blocking', 'creditors', `${p}.name`, row + 'не указан кредитор');
    if (!req(c.obligationType))
      add('blocking', 'creditors', `${p}.obligationType`, row + 'не указано содержание обязательства');
    if (!(c.amountDebt > 0))
      add('blocking', 'creditors', `${p}.amountDebt`, row + 'не указана текущая задолженность');
    // Просроченная часть — часть задолженности, больше неё быть не может
    if (c.amountOverdue !== undefined && c.amountOverdue > c.amountDebt)
      add('warning', 'creditors', `${p}.amountOverdue`, row + 'просрочено больше, чем вся задолженность');
    if (!req(c.basis)) add('warning', 'creditors', `${p}.basis`, row + 'не указано основание возникновения');
    if (!req(c.address)) add('warning', 'creditors', `${p}.address`, row + 'не указано место нахождения кредитора');
    if (!req(c.confirmingDocument))
      add('warning', 'creditors', `${p}.confirmingDocument`, row + 'не указан документ, подтверждающий задолженность');
    if (c.inn) {
      const msg = innError(c.inn);
      if (msg) add('warning', 'creditors', `${p}.inn`, row + msg);
    }
  });

  // Полные дубли: один и тот же долг попал в таблицу дважды — сумма задвоится
  for (const group of findDuplicateCreditors(profile.creditors)) {
    add('warning', 'creditors', 'creditors', `Строки ${group.map((i) => i + 1).join(', ')} полностью совпадают — проверьте, не внесён ли кредитор дважды`);
  }

  // Обязательные платежи входят в общую сумму долга (решение по вопросу 22)
  profile.obligatoryPayments.forEach((payment, i) => {
    const p = `obligatoryPayments[${i}]`;
    const row = `Обязательный платёж ${i + 1}: `;
    if (!req(payment.name))
      add('blocking', 'creditors', `${p}.name`, row + 'не указано наименование налога, сбора или взноса');
    // Строка «Пени» из справки ЕНС — без недоимки, только с пенями
    if (!(payment.arrears > 0) && !((payment.penalties ?? 0) > 0)) add('blocking', 'creditors', `${p}.arrears`, row + 'не указана недоимка');
  });

  if (!creditorsTotalsConsistent(profile))
    add('blocking', 'creditors', 'creditors', 'Сумма в заявлении не совпадает с суммой в списке кредиторов');

  if (totals.creditorsCount > 0 && totals.total < DEBT_THRESHOLD_KOPECKS)
    add(
      'warning',
      'creditors',
      'creditors',
      'Общая сумма задолженности менее 500 000 ₽ — обращение возможно только по праву должника ' +
        '(п. 2 ст. 213.4 Закона о банкротстве). Проверьте, достаточно ли обстоятельств, ' +
        'свидетельствующих о неплатёжеспособности',
    );

  // ---- Имущество -----------------------------------------------------------
  const p = profile.property;
  const propertyRows =
    p.realty.length +
    p.vehicles.length +
    p.accounts.length +
    p.shares.length +
    p.securities.length +
    p.valuables.length;

  if (!p.hasProperty && propertyRows > 0)
    add(
      'warning',
      'property',
      'property.hasProperty',
      'Указано отсутствие имущества, но разделы описи заполнены — заявление получит формулировки для дела без имущества',
    );
  if (p.hasProperty && propertyRows === 0)
    add('warning', 'property', 'property.hasProperty', 'Указано наличие имущества, но ни один раздел описи не заполнен');

  p.realty.forEach((r, i) => {
    if (!req(r.cadastralNumber))
      add('warning', 'property', `property.realty[${i}].cadastralNumber`, `Недвижимость, строка ${i + 1}: не указан кадастровый номер`);
    if (!req(r.address))
      add('warning', 'property', `property.realty[${i}].address`, `Недвижимость, строка ${i + 1}: не указан адрес`);
  });

  // ---- Сделки --------------------------------------------------------------
  if (profile.deals.hasDeals) {
    profile.deals.items.forEach((deal, i) => {
      if (!req(deal.subject))
        add('blocking', 'deals', `deals.items[${i}].subject`, `Сделка ${i + 1}: не указан предмет`);
      if (!isWithinThreeYears(deal.date, profile.legal.applicationDate, now))
        add('warning', 'deals', `deals.items[${i}].date`, `Сделка ${i + 1}: дата выходит за трёхлетний период до подачи заявления`);
      // Из выписки ФНС сделка приходит без цены и второй стороны (26.10.2026):
      // не блокирует, но напоминает дописать по договору
      if (!(deal.price > 0) || !req(deal.parties))
        add('warning', 'deals', `deals.items[${i}].parties`, `Сделка ${i + 1}: укажите цену и вторую сторону по договору`);
    });
  }

  // ---- Семья ---------------------------------------------------------------
  const f = profile.family;
  if (f.statuses.length === 0) add('blocking', 'family', 'family.statuses', 'Не указано семейное положение');
  if (f.statuses.includes('married') && f.spouses.filter((s) => s.relation === 'current').length === 0)
    add('blocking', 'family', 'family.spouses', 'Выбран статус «состоит в браке», но супруг не указан');
  if (f.statuses.includes('divorced') && f.spouses.filter((s) => s.relation === 'former').length === 0)
    add('blocking', 'family', 'family.spouses', 'Выбран статус «брак расторгнут», но бывший супруг не указан');

  f.spouses.forEach((s, i) => {
    const label = `Супруг ${i + 1}: `;
    if (!req(s.fio)) add('blocking', 'family', `family.spouses[${i}].fio`, label + 'не указано ФИО');
    // Опечатка в фамилии — свидетельство сканом не сверить (сверка 26.10.2026)
    const like = similarSurname(s.fio, [d.last]);
    if (like) add('warning', 'family', `family.spouses[${i}].fio`, label + `фамилия почти совпадает с фамилией должника «${like}» — сверьте со свидетельством о браке`);
    if (s.marriedAt && !isPast(s.marriedAt, now))
      add('warning', 'family', `family.spouses[${i}].marriedAt`, label + 'дата заключения брака в будущем');
    if (s.marriedAt && d.birthDate && !isBefore(d.birthDate, s.marriedAt))
      add('blocking', 'family', `family.spouses[${i}].marriedAt`, label + 'дата заключения брака раньше даты рождения должника');
    if (s.relation === 'former') {
      if (!req(s.divorcedAt))
        add('warning', 'family', `family.spouses[${i}].divorcedAt`, label + 'не указана дата расторжения брака');
      else if (s.marriedAt && !isBefore(s.marriedAt, s.divorcedAt))
        add('blocking', 'family', `family.spouses[${i}].divorcedAt`, label + 'дата расторжения раньше даты заключения брака');
    }
  });

  f.children.forEach((c, i) => {
    const label = `Ребёнок ${i + 1}: `;
    if (!req(c.fio)) add('blocking', 'family', `family.children[${i}].fio`, label + 'не указано ФИО');
    const like = similarSurname(c.fio, [d.last, ...f.spouses.map((s) => s.fio)]);
    if (like) add('warning', 'family', `family.children[${i}].fio`, label + `фамилия почти совпадает с «${like}» — сверьте со свидетельством о рождении`);
    if (!req(c.birthDate)) add('blocking', 'family', `family.children[${i}].birthDate`, label + 'не указана дата рождения');
    else if (!isPast(c.birthDate, now))
      add('blocking', 'family', `family.children[${i}].birthDate`, label + 'дата рождения в будущем');
  });

  // ---- Доходы --------------------------------------------------------------
  const inc = profile.income;
  if (inc.employed) {
    if (!req(inc.employerName))
      add('blocking', 'income', 'income.employerName', 'Не указано наименование работодателя');
    if (inc.employerInn) {
      const msg = innError(inc.employerInn);
      if (msg) add('warning', 'income', 'income.employerInn', 'Работодатель: ' + msg);
    } else if (req(inc.employerName)) {
      // ИНН работодателя печатается в заявлении (сверка 26.10.2026)
      add('warning', 'income', 'income.employerInn', 'Не указан ИНН работодателя — найдите по кнопке «Найти ИНН по названию»');
    }
    // Зарплата нужна, только если её решили указывать в заявлении
    if (inc.includeSalary && !(inc.monthlySalary > 0))
      add('warning', 'income', 'income.monthlySalary', 'Не указана среднемесячная заработная плата');
  }
  if (inc.hasSocialPayments && !req(inc.socialPaymentsDescription))
    add('blocking', 'income', 'income.socialPaymentsDescription', 'Указаны социальные выплаты, но не указано какие');
  if (inc.hasOrganizations)
    inc.organizations.forEach((o, i) => {
      if (!req(o.name))
        add('blocking', 'income', `income.organizations[${i}].name`, `Организация ${i + 1}: не указано наименование`);
      if (!req(o.share))
        add('warning', 'income', `income.organizations[${i}].share`, `Организация ${i + 1}: не указан размер доли`);
    });

  // ---- Процессуальное ------------------------------------------------------
  if (!req(profile.legal.sro.name)) add('blocking', 'legal', 'legal.sro', 'Не выбрана СРО арбитражных управляющих');
  // Дата подписи форм — дата подачи; без неё дата остаётся пустой (сверка 26.10.2026)
  if (!req(profile.legal.applicationDate))
    add('warning', 'legal', 'legal.applicationDate', 'Не указана дата подачи заявления — дата подписи в описи и списке кредиторов останется пустой');
  if (!req(profile.legal.creditBureau.name))
    add('warning', 'legal', 'legal.creditBureau', 'Не выбрано бюро кредитных историй — абзац со ссылкой на 218-ФЗ останется незаполненным');
  if (!req(profile.legal.creditReportKind))
    add('warning', 'legal', 'legal.creditReportKind', 'Не указан вид кредитного отчёта');

  // ---- Свод по блокам ------------------------------------------------------
  const blocks = {} as Readiness['blocks'];
  for (const id of Object.keys(BLOCK_TITLES) as BlockId[]) {
    const own = issues.filter((i) => i.block === id);
    if (own.some((i) => i.severity === 'blocking')) blocks[id] = 'errors';
    else if (own.some((i) => i.severity === 'warning')) blocks[id] = 'warnings';
    else blocks[id] = 'filled';
  }

  const blocking = issues.filter((i) => i.severity === 'blocking');
  const warnings = issues.filter((i) => i.severity === 'warning');

  return {
    issues,
    blocking,
    warnings,
    blocks,
    canGenerate: blocking.length === 0,
    totalKopecks: totals.total,
    creditorsCount: totals.creditorsCount,
  };
}
